<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import PaymentSettingsAPI from 'dashboard/api/paymentSettings';

const isLoading = ref(false);
const isSubmitting = ref(false);

// Form state
const paymentMode = ref('asaas');
const pixKey = ref('');
const pixKeyType = ref('cpf');
const pixEnabled = ref(true);
const pixMode = ref('key');
const cardEnabled = ref(true);
const maxInstallments = ref(12);
const debitEnabled = ref(true);
const debitFee = ref(0);
const cashEnabled = ref(true);
const promissoriaEnabled = ref(false);
const promissoriaMaxInstallments = ref(3);
const cardFees = ref({});
const asaasApiKey = ref('');
const asaasEnvironment = ref('production');
const asaasConfigured = ref(false);
const catalogLogoUrl = ref(null);
const catalogLogoFile = ref(null);
const removeLogo = ref(false);
const totenContactName = ref('');
const totenContactPhone = ref('');
const catalogHeaderColor = ref('#075E54');
const catalogTheme = ref('classic');
const orderConfirmationMessage = ref('');
const orderAcceptedMessage = ref('');
const orderOutForDeliveryMessage = ref('');
const orderDeliveredMessage = ref('');
const orderReceivedMessage = ref('');
const catalogInboxId = ref(null);
const catalogWhatsappTemplateName = ref('');
const catalogWhatsappTemplateLanguage = ref('pt_BR');
const whatsappInboxes = ref([]);

const selectedInboxHasTemplates = computed(() => {
  if (!catalogInboxId.value) return false;
  const inbox = whatsappInboxes.value.find(i => i.id === catalogInboxId.value);
  return inbox?.has_templates ?? false;
});

// Tema do Toten
const totenTheme = ref('default');
const totenPrimaryColor = ref('#075E54');
const totenWelcomeText = ref('CAIXA LIVRE');
const totenStartText = ref('Toque para Iniciar');
const totenIdleTimeout = ref(300);

const totenThemes = [
  { value: 'default', label: 'Padrao', desc: 'Verde escuro classico', icon: 'i-lucide-monitor', colors: { pri: '#075E54', bg: '#f0f2f5' } },
  { value: 'dark', label: 'Escuro', desc: 'Fundo escuro elegante', icon: 'i-lucide-moon', colors: { pri: '#6366f1', bg: '#0f172a' } },
  { value: 'neon', label: 'Neon', desc: 'Moderno com cores vibrantes', icon: 'i-lucide-zap', colors: { pri: '#06b6d4', bg: '#111827' } },
  { value: 'food', label: 'Alimentacao', desc: 'Quente para restaurantes', icon: 'i-lucide-utensils', colors: { pri: '#ea580c', bg: '#fef3c7' } },
  { value: 'pharmacy', label: 'Farmacia', desc: 'Limpo e profissional', icon: 'i-lucide-pill', colors: { pri: '#2563eb', bg: '#eff6ff' } },
];

const totenPrimaryColors = [
  { value: '#075E54', label: 'Verde Escuro' },
  { value: '#6366f1', label: 'Indigo' },
  { value: '#06b6d4', label: 'Ciano' },
  { value: '#ea580c', label: 'Laranja' },
  { value: '#2563eb', label: 'Azul' },
  { value: '#dc2626', label: 'Vermelho' },
  { value: '#7c3aed', label: 'Roxo' },
  { value: '#059669', label: 'Esmeralda' },
  { value: '#be185d', label: 'Rosa' },
  { value: '#1e293b', label: 'Escuro' },
];

// AutoTEF Stone
const autotefEnabled = ref(false);
const autotefStoneCode = ref('');
const autotefUrl = ref('http://localhost:8000');
const autotefPartnerName = ref('TOTEN-PDV 1.0');
const autotefConnectionName = ref('');
const autotefPixEnabled = ref(false);
const autotefPixExpiresIn = ref(600);

const presetColors = [
  { value: '#075E54', label: 'Verde Escuro' },
  { value: '#1a73e8', label: 'Azul' },
  { value: '#7c3aed', label: 'Roxo' },
  { value: '#dc2626', label: 'Vermelho' },
  { value: '#ea580c', label: 'Laranja' },
  { value: '#0891b2', label: 'Ciano' },
  { value: '#be185d', label: 'Rosa' },
  { value: '#1e293b', label: 'Escuro' },
  { value: '#059669', label: 'Esmeralda' },
  { value: '#4f46e5', label: 'Indigo' },
];

const weekdays = [
  { key: '1', label: 'Segunda-feira' },
  { key: '2', label: 'Terca-feira' },
  { key: '3', label: 'Quarta-feira' },
  { key: '4', label: 'Quinta-feira' },
  { key: '5', label: 'Sexta-feira' },
  { key: '6', label: 'Sabado' },
  { key: '0', label: 'Domingo' },
];

const defaultBusinessHour = { enabled: true, open: '07:00', close: '22:00' };
const businessHours = ref(
  Object.fromEntries(weekdays.map(({ key }) => [key, { ...defaultBusinessHour }]))
);

const pixKeyTypes = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave Aleatoria' },
];

const defaultCardFees = {
  1: 0,
  2: 2.99,
  3: 3.99,
  4: 4.99,
  5: 5.99,
  6: 6.99,
  7: 7.99,
  8: 8.99,
  9: 9.99,
  10: 10.99,
  11: 11.99,
  12: 12.99,
};

const showAsaasConfig = computed(() => paymentMode.value === 'asaas');
const showPaymentMethods = computed(
  () => paymentMode.value === 'asaas' || paymentMode.value === 'maquininha'
);
const showPixConfig = computed(
  () =>
    (paymentMode.value === 'asaas' || paymentMode.value === 'maquininha') &&
    pixEnabled.value
);
const showCardFees = computed(
  () =>
    paymentMode.value === 'asaas' && cardEnabled.value && asaasConfigured.value
);

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const response = await PaymentSettingsAPI.get();
    const data = response.data;

    paymentMode.value = data.payment_mode || 'asaas';
    pixKey.value = data.pix_key || '';
    pixKeyType.value = data.pix_key_type || 'cpf';
    pixEnabled.value = data.pix_enabled ?? true;
    pixMode.value = data.pix_mode || 'key';
    cardEnabled.value = data.card_enabled ?? true;
    maxInstallments.value = data.max_installments || 12;
    debitEnabled.value = data.debit_enabled ?? true;
    debitFee.value = data.debit_fee || 0;
    cashEnabled.value = data.cash_enabled ?? true;
    promissoriaEnabled.value = data.promissoria_enabled ?? false;
    promissoriaMaxInstallments.value = data.promissoria_max_installments || 3;
    cardFees.value = data.card_fees || defaultCardFees;
    asaasApiKey.value = '';
    asaasEnvironment.value = data.asaas_environment || 'production';
    asaasConfigured.value = data.asaas_configured ?? false;
    catalogLogoUrl.value = data.catalog_logo_url || null;
    totenContactName.value = data.toten_contact_name || '';
    totenContactPhone.value = data.toten_contact_phone || '';
    catalogHeaderColor.value = data.catalog_header_color || '#075E54';
    catalogTheme.value = data.catalog_theme || 'classic';
    orderConfirmationMessage.value = data.order_confirmation_message || 'Recebemos seu pedido! Estamos verificando os itens. Aguarde a confirmacao.';
    orderAcceptedMessage.value = data.order_accepted_message || 'Seu pedido *{pedido_id}* foi aceito! Estamos verificando os itens e preparando para envio. Aguarde!';
    orderOutForDeliveryMessage.value =
      data.order_out_for_delivery_message || 'Seu pedido *{pedido_id}* saiu para entrega! Em breve estara com voce.';
    orderDeliveredMessage.value = data.order_delivered_message || 'Seu pedido *{pedido_id}* foi entregue! Obrigado pela compra.';
    orderReceivedMessage.value = data.order_received_message || '';
    catalogInboxId.value = data.catalog_inbox_id || null;
    catalogWhatsappTemplateName.value = data.catalog_whatsapp_template_name || '';
    catalogWhatsappTemplateLanguage.value = data.catalog_whatsapp_template_language || 'pt_BR';
    whatsappInboxes.value = data.whatsapp_inboxes || [];

    // Tema do Toten
    totenTheme.value = data.toten_theme || 'default';
    totenPrimaryColor.value = data.toten_primary_color || '#075E54';
    totenWelcomeText.value = data.toten_welcome_text || 'CAIXA LIVRE';
    totenStartText.value = data.toten_start_text || 'Toque para Iniciar';
    totenIdleTimeout.value = data.toten_idle_timeout || 300;

    // AutoTEF Stone
    autotefEnabled.value = data.autotef_enabled ?? false;
    autotefStoneCode.value = '';
    autotefUrl.value = data.autotef_url || 'http://localhost:8000';
    autotefPartnerName.value = data.autotef_partner_name || 'TOTEN-PDV 1.0';
    autotefConnectionName.value = data.autotef_connection_name || '';
    autotefPixEnabled.value = data.autotef_pix_enabled ?? false;
    autotefPixExpiresIn.value = data.autotef_pix_expires_in || 600;

    const bh = data.business_hours || {};
    weekdays.forEach(({ key }) => {
      if (!bh[key]) {
        bh[key] = { enabled: true, open: '07:00', close: '22:00' };
      }
    });
    businessHours.value = bh;

    for (let i = 1; i <= 24; i++) {
      if (cardFees.value[String(i)] === undefined) {
        cardFees.value[String(i)] = defaultCardFees[String(i)] || 0;
      }
    }
  } catch (error) {
    console.error('Error loading payment settings:', error);
    cardFees.value = { ...defaultCardFees };
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    const filteredFees = {};
    for (let i = 1; i <= maxInstallments.value; i++) {
      filteredFees[String(i)] = Number(cardFees.value[String(i)] || 0);
    }

    const payload = {
      payment_mode: paymentMode.value,
      pix_key: pixKey.value,
      pix_key_type: pixKeyType.value,
      pix_enabled: pixEnabled.value,
      pix_mode: pixMode.value,
      card_enabled: cardEnabled.value,
      max_installments: maxInstallments.value,
      debit_enabled: debitEnabled.value,
      debit_fee: Number(debitFee.value),
      cash_enabled: cashEnabled.value,
      promissoria_enabled: promissoriaEnabled.value,
      promissoria_max_installments: Number(promissoriaMaxInstallments.value),
      card_fees: filteredFees,
      asaas_environment: asaasEnvironment.value,
      business_hours: businessHours.value,
      catalog_header_color: catalogHeaderColor.value,
      catalog_theme: catalogTheme.value,
      order_confirmation_message: orderConfirmationMessage.value,
      order_accepted_message: orderAcceptedMessage.value,
      order_out_for_delivery_message: orderOutForDeliveryMessage.value,
      order_delivered_message: orderDeliveredMessage.value,
      order_received_message: orderReceivedMessage.value,
      catalog_inbox_id: catalogInboxId.value,
      catalog_whatsapp_template_name: catalogWhatsappTemplateName.value,
      catalog_whatsapp_template_language: catalogWhatsappTemplateLanguage.value,
      toten_contact_name: totenContactName.value,
      toten_contact_phone: totenContactPhone.value,
      toten_theme: totenTheme.value,
      toten_primary_color: totenPrimaryColor.value,
      toten_welcome_text: totenWelcomeText.value,
      toten_start_text: totenStartText.value,
      toten_idle_timeout: Number(totenIdleTimeout.value),
      autotef_enabled: autotefEnabled.value,
      autotef_url: autotefUrl.value,
      autotef_partner_name: autotefPartnerName.value,
      autotef_connection_name: autotefConnectionName.value,
      autotef_pix_enabled: autotefPixEnabled.value,
      autotef_pix_expires_in: Number(autotefPixExpiresIn.value),
    };

    if (asaasApiKey.value && !asaasApiKey.value.startsWith('••')) {
      payload.asaas_api_key = asaasApiKey.value;
    }

    if (autotefStoneCode.value && !autotefStoneCode.value.startsWith('••')) {
      payload.autotef_stone_code = autotefStoneCode.value;
    }

    if (catalogLogoFile.value || removeLogo.value) {
      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        const val = payload[key];
        if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
          formData.append(`payment_settings[${key}]`, JSON.stringify(val));
        } else {
          formData.append(`payment_settings[${key}]`, val);
        }
      });
      if (catalogLogoFile.value) {
        formData.append('catalog_logo', catalogLogoFile.value);
      }
      if (removeLogo.value) {
        formData.append('remove_catalog_logo', 'true');
      }
      await PaymentSettingsAPI.updateWithFormData(formData);
      catalogLogoFile.value = null;
      removeLogo.value = false;
    } else {
      await PaymentSettingsAPI.update(payload);
    }

    await loadSettings();
    useAlert('Configuracoes salvas com sucesso!');
  } catch (error) {
    console.error('Error saving payment settings:', error);
    useAlert('Erro ao salvar configuracoes');
  } finally {
    isSubmitting.value = false;
  }
};

const useDefaultFees = () => {
  cardFees.value = { ...defaultCardFees };
};

function onLogoChange(event) {
  const file = event.target.files[0];
  if (file) {
    catalogLogoFile.value = file;
    catalogLogoUrl.value = URL.createObjectURL(file);
    removeLogo.value = false;
  }
}

function onRemoveLogo() {
  catalogLogoFile.value = null;
  catalogLogoUrl.value = null;
  removeLogo.value = true;
}

function deactivateAsaas() {
  paymentMode.value = 'maquininha';
  asaasConfigured.value = false;
}

onMounted(() => {
  loadSettings();
});
</script>

<template>
  <SectionLayout
    title="Configuracoes de Pagamento"
    description="Configure o modo de pagamento, formas aceitas e integracao com Asaas."
    with-border
  >
    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <form v-else class="grid gap-6" @submit.prevent="handleSubmit">
      <!-- Logo do Catalogo -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-image text-blue-500" />
          Logo do Catalogo
        </h4>
        <div class="flex items-center gap-4 pl-6">
          <div
            class="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-n-weak bg-n-alpha-2"
          >
            <img
              v-if="catalogLogoUrl"
              :src="catalogLogoUrl"
              alt="Logo"
              class="h-full w-full object-contain"
            />
            <span v-else class="i-lucide-building-2 text-2xl text-n-slate-8" />
          </div>
          <div class="flex flex-col gap-2">
            <label
              class="cursor-pointer rounded-lg bg-n-alpha-2 px-3 py-1.5 text-xs font-semibold text-n-slate-12 hover:bg-n-alpha-3"
            >
              Selecionar logo
              <input
                type="file"
                accept="image/*"
                class="hidden"
                @change="onLogoChange"
              />
            </label>
            <button
              v-if="catalogLogoUrl"
              type="button"
              class="text-xs text-n-ruby-9 hover:text-n-ruby-11"
              @click="onRemoveLogo"
            >
              Remover logo
            </button>
          </div>
        </div>
      </div>

      <!-- Cliente Toten -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-monitor text-emerald-500" />
          Cliente Toten
        </h4>
        <p class="text-xs text-n-slate-10 pl-6">
          Configure o contato padrao para vendas realizadas pelo Toten de autoatendimento.
          Todas as vendas no Toten serao registradas para este contato.
        </p>
        <div class="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              Nome do Cliente
            </label>
            <input
              v-model="totenContactName"
              type="text"
              placeholder="Ex: Cliente Toten"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-white dark:bg-slate-800 outline-none focus:border-woot-500 focus:ring-2 focus:ring-woot-100"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              Telefone
            </label>
            <input
              v-model="totenContactPhone"
              type="tel"
              placeholder="Ex: +5562999999999"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-white dark:bg-slate-800 outline-none focus:border-woot-500 focus:ring-2 focus:ring-woot-100"
            />
          </div>
        </div>
      </div>

      <!-- Tema do Toten -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-palette text-purple-500" />
          Tema do Toten
        </h4>
        <p class="text-xs text-n-slate-10 pl-6">
          Personalize a aparencia do totem de autoatendimento.
        </p>

        <!-- Theme selector -->
        <div class="pl-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <button
            v-for="theme in totenThemes"
            :key="theme.value"
            type="button"
            class="flex flex-col items-center gap-2 rounded-xl border-2 p-3 transition-all hover:shadow-md"
            :class="
              totenTheme === theme.value
                ? 'border-woot-500 bg-woot-25 shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="totenTheme = theme.value; totenPrimaryColor = theme.colors.pri"
          >
            <div
              class="flex h-16 w-full items-center justify-center rounded-lg overflow-hidden"
              :style="{ backgroundColor: theme.colors.bg }"
            >
              <div class="flex flex-col items-center gap-1">
                <div
                  class="h-2 w-full"
                  :style="{ backgroundColor: theme.colors.pri }"
                />
                <div class="flex gap-0.5 px-1">
                  <div
                    class="h-6 w-5 rounded-sm border"
                    :style="{ borderColor: theme.colors.pri + '40', backgroundColor: theme.colors.bg }"
                  />
                  <div
                    class="h-6 w-5 rounded-sm border"
                    :style="{ borderColor: theme.colors.pri + '40', backgroundColor: theme.colors.bg }"
                  />
                  <div
                    class="h-6 w-5 rounded-sm border"
                    :style="{ borderColor: theme.colors.pri + '40', backgroundColor: theme.colors.bg }"
                  />
                </div>
                <div
                  class="h-2 w-8 rounded-full"
                  :style="{ backgroundColor: theme.colors.pri }"
                />
              </div>
            </div>
            <div class="text-center">
              <div class="text-xs font-semibold text-n-slate-12">{{ theme.label }}</div>
              <div class="text-[10px] text-n-slate-9 leading-tight">{{ theme.desc }}</div>
            </div>
            <span
              v-if="totenTheme === theme.value"
              class="i-lucide-check-circle text-woot-500 text-sm"
            />
          </button>
        </div>

        <!-- Cor principal do Toten -->
        <div class="pl-6 space-y-3">
          <label class="text-xs font-medium text-n-slate-11">
            Cor Principal do Toten
          </label>
          <div class="flex items-center gap-3">
            <div
              class="h-10 w-10 shrink-0 rounded-lg border-2 border-n-weak shadow-sm"
              :style="{ backgroundColor: totenPrimaryColor }"
            />
            <input
              v-model="totenPrimaryColor"
              type="color"
              class="h-10 w-14 cursor-pointer rounded-lg border border-n-weak"
            />
            <NextInput
              v-model="totenPrimaryColor"
              class="w-32"
              placeholder="#075E54"
              maxlength="7"
            />
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="color in totenPrimaryColors"
              :key="color.value"
              type="button"
              class="group relative h-7 w-7 rounded-full border-2 transition-all hover:scale-110"
              :class="
                totenPrimaryColor === color.value
                  ? 'border-n-slate-12 ring-2 ring-offset-2 ring-n-blue-9'
                  : 'border-transparent hover:border-n-slate-6'
              "
              :style="{ backgroundColor: color.value }"
              :title="color.label"
              @click="totenPrimaryColor = color.value"
            >
              <span
                v-if="totenPrimaryColor === color.value"
                class="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold"
              >
                &#10003;
              </span>
            </button>
          </div>
        </div>

        <!-- Textos personalizaveis -->
        <div class="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              Texto de Boas-vindas
            </label>
            <NextInput
              v-model="totenWelcomeText"
              type="text"
              class="w-full"
              placeholder="CAIXA LIVRE"
            />
            <span class="text-[10px] text-n-slate-9">
              Exibido na tela inicial do totem.
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              Texto do Botao Iniciar
            </label>
            <NextInput
              v-model="totenStartText"
              type="text"
              class="w-full"
              placeholder="Toque para Iniciar"
            />
          </div>
        </div>

        <!-- Timeout inatividade -->
        <div class="pl-6">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              Tempo de Inatividade (segundos)
            </label>
            <div class="flex items-center gap-3">
              <NextInput
                v-model.number="totenIdleTimeout"
                type="number"
                min="30"
                max="3600"
                class="w-32"
                placeholder="300"
              />
              <span class="text-xs text-n-slate-9">
                {{ Math.floor(totenIdleTimeout / 60) }}min {{ totenIdleTimeout % 60 }}s — volta a tela inicial apos inatividade
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- AutoTEF Stone – Integração Pinpad -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-nfc text-blue-500" />
          AutoTEF Stone (Pinpad)
        </h4>
        <p class="text-xs text-n-slate-10 pl-6">
          Configure a integracao com o pinpad Stone via AutoTEF para pagamentos
          por cartao (debito, credito, voucher) e PIX diretamente no totem de autoatendimento.
        </p>

        <div class="pl-6 space-y-4">
          <!-- Toggle -->
          <div class="flex items-center gap-3 rounded-lg border border-n-weak p-3">
            <Checkbox v-model="autotefEnabled" />
            <span class="text-sm font-medium text-n-slate-12">
              Habilitar AutoTEF Stone
            </span>
          </div>

          <template v-if="autotefEnabled">
            <!-- Stone Code -->
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">
                Stone Code (Codigo de Credenciamento)
              </label>
              <NextInput
                v-model="autotefStoneCode"
                type="password"
                class="w-full"
                :placeholder="
                  autotefStoneCode
                    ? 'Codigo configurado (deixe vazio para manter)'
                    : 'Digite o Stone Code'
                "
              />
              <span class="text-[10px] text-n-slate-9">
                Codigo fornecido pela Stone para identificar o estabelecimento.
              </span>
            </div>

            <!-- URL do AutoTEF -->
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">
                URL do AutoTEF Slim
              </label>
              <NextInput
                v-model="autotefUrl"
                type="text"
                class="w-full"
                placeholder="http://localhost:8000"
              />
              <span class="text-[10px] text-n-slate-9">
                Endereco do servico AutoTEF Slim rodando localmente no totem.
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- Partner Name -->
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11">
                  Nome da Aplicacao (Partner)
                </label>
                <NextInput
                  v-model="autotefPartnerName"
                  type="text"
                  class="w-full"
                  placeholder="TOTEN-PDV 1.0"
                />
              </div>

              <!-- Connection Name (porta) -->
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11">
                  Porta do Pinpad
                </label>
                <NextInput
                  v-model="autotefConnectionName"
                  type="text"
                  class="w-full"
                  placeholder="COM4 ou /dev/ttyACM0"
                />
                <span class="text-[10px] text-n-slate-9">
                  Opcional – deixe vazio para auto-detecção.
                </span>
              </div>
            </div>

            <!-- PIX via Stone Banking -->
            <div class="rounded-lg border border-n-weak p-3 space-y-3">
              <div class="flex items-center gap-3">
                <Checkbox v-model="autotefPixEnabled" />
                <span class="text-sm font-medium text-n-slate-12">
                  PIX via Stone Banking
                </span>
              </div>
              <p class="text-[10px] text-n-slate-9 pl-7">
                Habilita pagamento PIX pelo AutoTEF usando o Stone Banking.
                Requer configuracao previa na Stone.
              </p>
              <div v-if="autotefPixEnabled" class="pl-7">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">
                    Tempo de expiracao do QR Code (segundos)
                  </label>
                  <NextInput
                    v-model.number="autotefPixExpiresIn"
                    type="number"
                    min="60"
                    max="3600"
                    class="w-32"
                    placeholder="600"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Cor da Barra Superior -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-palette text-violet-500" />
          Cor da Barra Superior
        </h4>
        <div class="pl-6 space-y-3">
          <div class="flex items-center gap-3">
            <div
              class="h-10 w-10 shrink-0 rounded-lg border-2 border-n-weak shadow-sm"
              :style="{ backgroundColor: catalogHeaderColor }"
            />
            <input
              v-model="catalogHeaderColor"
              type="color"
              class="h-10 w-14 cursor-pointer rounded-lg border border-n-weak"
            />
            <NextInput
              v-model="catalogHeaderColor"
              class="w-32"
              placeholder="#075E54"
              maxlength="7"
            />
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="color in presetColors"
              :key="color.value"
              type="button"
              class="group relative h-8 w-8 rounded-full border-2 transition-all hover:scale-110"
              :class="
                catalogHeaderColor === color.value
                  ? 'border-n-slate-12 ring-2 ring-offset-2 ring-n-blue-9'
                  : 'border-transparent hover:border-n-slate-6'
              "
              :style="{ backgroundColor: color.value }"
              :title="color.label"
              @click="catalogHeaderColor = color.value"
            >
              <span
                v-if="catalogHeaderColor === color.value"
                class="absolute inset-0 flex items-center justify-center text-white text-xs font-bold"
              >
                &#10003;
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Tema do Catalogo -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-layout-template text-teal-500" />
          Tema do Catalogo
        </h4>
        <p class="text-xs text-n-slate-10 pl-6">
          Escolha o modelo visual do seu catalogo publico.
        </p>
        <div class="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            class="flex flex-col items-center gap-3 rounded-xl border-2 p-4 transition-all hover:shadow-md"
            :class="
              catalogTheme === 'classic'
                ? 'border-woot-500 bg-woot-25 shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="catalogTheme = 'classic'"
          >
            <div
              class="flex h-24 w-full items-center justify-center rounded-lg bg-n-alpha-2"
            >
              <div class="flex flex-col items-center gap-1">
                <div
                  class="h-3 w-full rounded-t-md"
                  :style="{ backgroundColor: catalogHeaderColor }"
                />
                <div class="flex gap-1 px-2">
                  <div
                    class="h-10 w-8 rounded bg-n-alpha-3 border border-n-weak"
                  />
                  <div
                    class="h-10 w-8 rounded bg-n-alpha-3 border border-n-weak"
                  />
                  <div
                    class="h-10 w-8 rounded bg-n-alpha-3 border border-n-weak"
                  />
                </div>
              </div>
            </div>
            <div class="text-center">
              <div class="text-sm font-semibold text-n-slate-12">Classico</div>
              <div class="text-xs text-n-slate-9">
                Cards verticais em carrossel
              </div>
            </div>
            <span
              v-if="catalogTheme === 'classic'"
              class="i-lucide-check-circle text-woot-500 text-lg"
            />
          </button>
          <button
            type="button"
            class="flex flex-col items-center gap-3 rounded-xl border-2 p-4 transition-all hover:shadow-md"
            :class="
              catalogTheme === 'cardapio'
                ? 'border-woot-500 bg-woot-25 shadow-sm'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="catalogTheme = 'cardapio'"
          >
            <div
              class="flex h-24 w-full items-center justify-center rounded-lg bg-n-alpha-2"
            >
              <div class="flex flex-col items-center gap-1 w-full px-2">
                <div
                  class="h-5 w-8 rounded-full mx-auto"
                  :style="{ backgroundColor: catalogHeaderColor }"
                />
                <div class="flex gap-1 text-[6px] text-n-slate-8">
                  <span class="border-b border-current px-1">Cat1</span>
                  <span class="px-1">Cat2</span>
                  <span class="px-1">Cat3</span>
                </div>
                <div class="flex flex-col gap-1 w-full">
                  <div
                    class="flex items-center gap-1 rounded border border-n-weak bg-white p-1"
                  >
                    <div class="flex-1 space-y-0.5">
                      <div class="h-1.5 w-10 rounded bg-n-alpha-4" />
                      <div class="h-1 w-6 rounded bg-n-alpha-2" />
                    </div>
                    <div
                      class="h-5 w-5 rounded bg-n-alpha-3 border border-n-weak shrink-0"
                    />
                  </div>
                  <div
                    class="flex items-center gap-1 rounded border border-n-weak bg-white p-1"
                  >
                    <div class="flex-1 space-y-0.5">
                      <div class="h-1.5 w-10 rounded bg-n-alpha-4" />
                      <div class="h-1 w-6 rounded bg-n-alpha-2" />
                    </div>
                    <div
                      class="h-5 w-5 rounded bg-n-alpha-3 border border-n-weak shrink-0"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="text-center">
              <div class="text-sm font-semibold text-n-slate-12">Cardapio</div>
              <div class="text-xs text-n-slate-9">
                Lista horizontal estilo WhatsMenu
              </div>
            </div>
            <span
              v-if="catalogTheme === 'cardapio'"
              class="i-lucide-check-circle text-woot-500 text-lg"
            />
          </button>
        </div>
      </div>

      <!-- Horario de Funcionamento -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-clock text-amber-500" />
          Horario de Funcionamento
        </h4>
        <div class="grid gap-2 pl-6">
          <div
            v-for="day in weekdays"
            :key="day.key"
            class="flex items-center gap-3 rounded-lg border border-n-weak px-3 py-2"
          >
            <input
              v-model="businessHours[day.key].enabled"
              type="checkbox"
              class="h-4 w-4"
            />
            <span
              class="w-28 text-sm font-medium"
              :class="
                businessHours[day.key].enabled
                  ? 'text-n-slate-12'
                  : 'text-n-slate-8'
              "
            >
              {{ day.label }}
            </span>
            <input
              v-model="businessHours[day.key].open"
              type="time"
              class="rounded-md border border-n-weak px-2 py-1 text-sm"
              :disabled="!businessHours[day.key].enabled"
            />
            <span class="text-xs text-n-slate-8">ate</span>
            <input
              v-model="businessHours[day.key].close"
              type="time"
              class="rounded-md border border-n-weak px-2 py-1 text-sm"
              :disabled="!businessHours[day.key].enabled"
            />
          </div>
        </div>
      </div>

      <!-- Modo de Pagamento -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-wallet text-indigo-500" />
          Modo de Pagamento
        </h4>
        <div class="grid grid-cols-3 gap-3 pl-6">
          <button
            type="button"
            class="flex flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-center transition-all"
            :class="
              paymentMode === 'asaas'
                ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-10 hover:border-n-slate-6'
            "
            @click="paymentMode = 'asaas'"
          >
            <span class="i-lucide-credit-card text-2xl" />
            <span class="text-xs font-semibold">Asaas</span>
            <span class="text-[10px] leading-tight opacity-70"
              >Pagamento online</span
            >
          </button>
          <button
            type="button"
            class="flex flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-center transition-all"
            :class="
              paymentMode === 'maquininha'
                ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-10 hover:border-n-slate-6'
            "
            @click="paymentMode = 'maquininha'"
          >
            <span class="i-lucide-smartphone text-2xl" />
            <span class="text-xs font-semibold">Maquininha</span>
            <span class="text-[10px] leading-tight opacity-70"
              >Pagamento presencial</span
            >
          </button>
          <button
            type="button"
            class="flex flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-center transition-all"
            :class="
              paymentMode === 'whatsapp'
                ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-10 hover:border-n-slate-6'
            "
            @click="paymentMode = 'whatsapp'"
          >
            <span class="i-lucide-message-circle text-2xl" />
            <span class="text-xs font-semibold">WhatsApp</span>
            <span class="text-[10px] leading-tight opacity-70"
              >Pedido via WhatsApp</span
            >
          </button>
        </div>
        <p
          v-if="paymentMode === 'whatsapp'"
          class="pl-6 text-xs text-n-slate-9"
        >
          O pedido sera enviado diretamente para o WhatsApp da caixa de entrada
          selecionada no catalogo.
        </p>
      </div>

      <!-- Asaas Integration (only for asaas mode) -->
      <div v-if="showAsaasConfig" class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-credit-card text-emerald-500" />
          Integracao Asaas
        </h4>

        <div class="grid gap-4 pl-6">
          <div
            v-if="asaasConfigured"
            class="flex items-center justify-between gap-2 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20"
          >
            <div class="flex items-center gap-2 text-sm text-green-600">
              <span class="i-lucide-check-circle" />
              Asaas configurado e ativo
            </div>
            <button
              type="button"
              class="text-xs text-n-ruby-9 hover:text-n-ruby-11 font-medium"
              @click="deactivateAsaas"
            >
              Desativar
            </button>
          </div>

          <WithLabel
            label="Ambiente"
            help-message="Use 'sandbox' para testes e 'production' para ambiente real"
          >
            <select
              v-model="asaasEnvironment"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
            >
              <option value="production" class="bg-white dark:bg-slate-800">
                Production
              </option>
              <option value="sandbox" class="bg-white dark:bg-slate-800">
                Sandbox (Testes)
              </option>
            </select>
          </WithLabel>

          <WithLabel
            label="API Key do Asaas"
            help-message="Chave de acesso da API Asaas. Obtenha em Minha Conta > Integracoes no painel Asaas."
          >
            <NextInput
              v-model="asaasApiKey"
              type="password"
              class="w-full"
              :placeholder="
                asaasConfigured
                  ? 'Chave configurada (deixe vazio para manter)'
                  : 'Cole sua API Key aqui'
              "
            />
          </WithLabel>
        </div>
      </div>

      <!-- Formas de Pagamento (asaas or maquininha) -->
      <div v-if="showPaymentMethods" class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-list-checks text-violet-500" />
          Formas de Pagamento Aceitas
        </h4>

        <div class="grid gap-3 pl-6">
          <!-- PIX -->
          <div class="rounded-lg border border-n-weak p-3">
            <div class="flex items-center gap-3">
              <Checkbox v-model="pixEnabled" />
              <span class="text-sm font-medium text-n-slate-12">PIX</span>
            </div>
            <div v-if="showPixConfig" class="mt-3 grid gap-3 pl-7">
              <!-- PIX mode selector (only for maquininha) -->
              <div
                v-if="paymentMode === 'maquininha'"
                class="grid grid-cols-2 gap-2"
              >
                <button
                  type="button"
                  class="flex flex-col items-center gap-1.5 rounded-lg border-2 px-3 py-3 text-center transition-all"
                  :class="
                    pixMode === 'machine'
                      ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                      : 'border-n-weak bg-n-alpha-1 text-n-slate-10 hover:border-n-slate-6'
                  "
                  @click="pixMode = 'machine'"
                >
                  <span class="i-lucide-smartphone text-lg" />
                  <span class="text-xs font-semibold">Na Maquininha</span>
                  <span class="text-[10px] leading-tight opacity-70"
                    >Cliente paga PIX na maquininha</span
                  >
                </button>
                <button
                  type="button"
                  class="flex flex-col items-center gap-1.5 rounded-lg border-2 px-3 py-3 text-center transition-all"
                  :class="
                    pixMode === 'key'
                      ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                      : 'border-n-weak bg-n-alpha-1 text-n-slate-10 hover:border-n-slate-6'
                  "
                  @click="pixMode = 'key'"
                >
                  <span class="i-lucide-key text-lg" />
                  <span class="text-xs font-semibold">Chave PIX</span>
                  <span class="text-[10px] leading-tight opacity-70"
                    >Cliente copia a chave e transfere</span
                  >
                </button>
              </div>

              <!-- PIX key fields (for asaas or maquininha key mode) -->
              <template
                v-if="
                  paymentMode === 'asaas' ||
                  (paymentMode === 'maquininha' && pixMode === 'key')
                "
              >
                <WithLabel label="Tipo de Chave PIX">
                  <select
                    v-model="pixKeyType"
                    class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
                  >
                    <option
                      v-for="type in pixKeyTypes"
                      :key="type.value"
                      :value="type.value"
                      class="bg-white dark:bg-slate-800"
                    >
                      {{ type.label }}
                    </option>
                  </select>
                </WithLabel>
                <WithLabel label="Chave PIX">
                  <NextInput
                    v-model="pixKey"
                    type="text"
                    class="w-full"
                    placeholder="Digite sua chave PIX"
                  />
                </WithLabel>
              </template>
            </div>
          </div>

          <!-- Cartao de Credito -->
          <div class="rounded-lg border border-n-weak p-3">
            <div class="flex items-center gap-3">
              <Checkbox v-model="cardEnabled" />
              <span class="text-sm font-medium text-n-slate-12"
                >Cartao de Credito</span
              >
            </div>
            <div v-if="cardEnabled" class="mt-3 grid gap-3 pl-7">
              <WithLabel
                label="Maximo de Parcelas"
                help-message="Quantidade maxima de parcelas permitidas"
              >
                <select
                  v-model="maxInstallments"
                  class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
                >
                  <option
                    v-for="n in 24"
                    :key="n"
                    :value="n"
                    class="bg-white dark:bg-slate-800"
                  >
                    {{ n }}x
                  </option>
                </select>
              </WithLabel>

              <!-- Card fees only for asaas mode -->
              <div v-if="showCardFees" class="space-y-3">
                <div class="flex items-center justify-between">
                  <p class="text-sm font-medium text-n-slate-11">
                    Taxa por Parcela (%)
                  </p>
                  <NextButton
                    type="button"
                    faded
                    slate
                    xs
                    label="Usar taxas padrao"
                    icon="i-lucide-refresh-cw"
                    @click="useDefaultFees"
                  />
                </div>
                <div
                  class="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-auto p-2 bg-n-alpha-1 rounded-lg"
                >
                  <div
                    v-for="n in maxInstallments"
                    :key="n"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-xs text-n-slate-10">{{ n }}x</label>
                    <input
                      v-model.number="cardFees[String(n)]"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      class="w-full rounded border border-n-weak bg-white dark:bg-slate-800 px-2 py-1.5 text-sm text-n-slate-12"
                      :placeholder="n === 1 ? '0' : ''"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Cartao de Debito -->
          <div class="rounded-lg border border-n-weak p-3">
            <div class="flex items-center gap-3">
              <Checkbox v-model="debitEnabled" />
              <span class="text-sm font-medium text-n-slate-12"
                >Cartao de Debito</span
              >
            </div>
            <div
              v-if="debitEnabled && paymentMode === 'asaas'"
              class="mt-3 pl-7"
            >
              <WithLabel label="Taxa do Debito (%)">
                <NextInput
                  v-model.number="debitFee"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  class="w-32"
                  placeholder="0.00"
                />
              </WithLabel>
            </div>
          </div>

          <!-- Dinheiro -->
          <div class="rounded-lg border border-n-weak p-3">
            <div class="flex items-center gap-3">
              <Checkbox v-model="cashEnabled" />
              <span class="text-sm font-medium text-n-slate-12">Dinheiro</span>
            </div>
          </div>

          <!-- Promissoria -->
          <div class="rounded-lg border border-n-weak p-3">
            <div class="flex items-center gap-3">
              <Checkbox v-model="promissoriaEnabled" />
              <span class="text-sm font-medium text-n-slate-12"
                >Promissoria</span
              >
            </div>
            <div v-if="promissoriaEnabled" class="mt-3 pl-7">
              <WithLabel label="Maximo de Parcelas">
                <select
                  v-model="promissoriaMaxInstallments"
                  class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
                >
                  <option
                    v-for="n in 24"
                    :key="n"
                    :value="n"
                    class="bg-white dark:bg-slate-800"
                  >
                    {{ n }}x
                  </option>
                </select>
              </WithLabel>
            </div>
          </div>
        </div>
      </div>

      <!-- Notificacao WhatsApp do Catalogo -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-send text-green-500" />
          Notificacao de Pedido via WhatsApp
        </h4>
        <p class="text-xs text-n-slate-10 pl-6">
          Configure qual inbox do WhatsApp sera usada para enviar notificacoes de pedidos do catalogo
          e o modelo de mensagem para cada situacao.
        </p>

        <div class="pl-6 space-y-4">
          <!-- Inbox selector -->
          <WithLabel
            label="Inbox do WhatsApp para Catalogo"
            help-message="Selecione qual caixa de entrada do WhatsApp sera usada para enviar notificacoes de pedidos. Se nao selecionar, sera usada a primeira disponivel."
          >
            <select
              v-model="catalogInboxId"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
            >
              <option :value="null" class="bg-white dark:bg-slate-800">
                Automatico (primeira disponivel)
              </option>
              <option
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                :value="inbox.id"
                class="bg-white dark:bg-slate-800"
              >
                {{ inbox.name }} ({{ inbox.type === 'whatsapp' ? 'API Oficial' : 'QuePasa' }})
                {{ inbox.has_templates ? '' : '- sem templates' }}
              </option>
            </select>
          </WithLabel>

          <!-- Order received message -->
          <WithLabel
            label="Mensagem de Pedido Recebido"
            help-message="Texto enviado quando um novo pedido e criado pelo catalogo. Deixe vazio para usar a mensagem padrao."
          >
            <textarea
              v-model="orderReceivedMessage"
              rows="4"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
              placeholder="📋 *Novo pedido #{pedido_id} recebido!*&#10;&#10;{itens}&#10;&#10;*Total: {total}*&#10;&#10;Seu pedido foi registrado e esta aguardando confirmacao. Obrigado!"
            />
            <p class="text-[10px] text-n-slate-10 mt-1">
              Variaveis:
              <code class="bg-n-alpha-2 px-1 rounded">{pedido_id}</code>,
              <code class="bg-n-alpha-2 px-1 rounded">{itens}</code>,
              <code class="bg-n-alpha-2 px-1 rounded">{total}</code>,
              <code class="bg-n-alpha-2 px-1 rounded">{cliente}</code>,
              <code class="bg-n-alpha-2 px-1 rounded">{taxa_entrega}</code>
            </p>
          </WithLabel>

          <!-- Template WhatsApp config -->
          <div class="rounded-lg border border-n-weak p-4 space-y-3">
            <h5 class="text-xs font-semibold text-n-slate-12 flex items-center gap-2">
              <span class="i-lucide-file-text text-blue-500" />
              Template WhatsApp (primeiro contato)
            </h5>
            <p class="text-[10px] text-n-slate-9 leading-relaxed">
              Quando nao houver conversa aberta (janela de 24h fechada), o sistema
              usara um template aprovado da API oficial do WhatsApp para enviar a
              notificacao. Se ja houver conversa aberta, sera usado o texto normal acima.
            </p>

            <div
              v-if="catalogInboxId && !selectedInboxHasTemplates"
              class="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-900/20"
            >
              <span class="i-lucide-alert-triangle text-amber-500" />
              <span class="text-xs text-amber-700 dark:text-amber-400">
                A inbox selecionada nao suporta templates (QuePasa). O template so funciona com a API oficial do WhatsApp.
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11">
                  Nome do Template
                </label>
                <NextInput
                  v-model="catalogWhatsappTemplateName"
                  type="text"
                  class="w-full"
                  placeholder="Ex: pedido_recebido"
                />
                <span class="text-[10px] text-n-slate-9">
                  Nome exato do template aprovado na Meta. Deixe vazio para desativar.
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-11">
                  Idioma do Template
                </label>
                <select
                  v-model="catalogWhatsappTemplateLanguage"
                  class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2.5 text-sm text-n-slate-12"
                >
                  <option value="pt_BR" class="bg-white dark:bg-slate-800">Portugues (BR)</option>
                  <option value="en_US" class="bg-white dark:bg-slate-800">English (US)</option>
                  <option value="es" class="bg-white dark:bg-slate-800">Espanol</option>
                </select>
              </div>
            </div>

            <div
              v-if="catalogWhatsappTemplateName"
              class="rounded-lg bg-n-alpha-2 p-2.5"
            >
              <p class="text-[10px] text-n-slate-10">
                O template recebera 3 parametros no body:
                <strong>{{1}}</strong> = numero do pedido,
                <strong>{{2}}</strong> = lista de itens,
                <strong>{{3}}</strong> = valor total.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Mensagens do Pedido -->
      <div class="space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-message-square-text text-teal-500" />
          Mensagens do Pedido (Loja Online)
        </h4>
        <div class="pl-6 space-y-4">
          <p class="text-xs text-n-slate-10 rounded-lg bg-n-alpha-2 p-2.5">
            Variaveis disponiveis:
            <code class="text-[10px]">{pedido_id}</code>,
            <code class="text-[10px]">{cliente}</code>,
            <code class="text-[10px]">{total}</code>,
            <code class="text-[10px]">{forma_pagamento}</code>. Deixe vazio para
            usar a mensagem padrao.
          </p>

          <WithLabel label="Tela de confirmacao (cliente ve no checkout)">
            <textarea
              v-model="orderConfirmationMessage"
              rows="2"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
              placeholder="Recebemos seu pedido! Estamos verificando os itens. Aguarde a confirmacao."
            />
          </WithLabel>

          <WithLabel label="Pedido aceito (enviada no chat ao aceitar)">
            <textarea
              v-model="orderAcceptedMessage"
              rows="2"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
              placeholder="Seu pedido *{pedido_id}* foi aceito! Estamos verificando os itens e preparando para envio. Aguarde!"
            />
          </WithLabel>

          <WithLabel label="Saiu para entrega (enviada no chat)">
            <textarea
              v-model="orderOutForDeliveryMessage"
              rows="2"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
              placeholder="Seu pedido *{pedido_id}* saiu para entrega! Em breve estara com voce."
            />
          </WithLabel>

          <WithLabel label="Pedido entregue (enviada no chat)">
            <textarea
              v-model="orderDeliveredMessage"
              rows="2"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 dark:bg-slate-800 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
              placeholder="Seu pedido *{pedido_id}* foi entregue! Obrigado pela compra."
            />
          </WithLabel>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex gap-2 pt-4 border-t border-n-weak">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          label="Salvar Configuracoes"
          icon="i-lucide-save"
        />
      </div>
    </form>
  </SectionLayout>
</template>

<style scoped>
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

select option {
  background-color: var(--n-solid-1);
  color: var(--n-slate-12);
}

:deep(.dark) select option,
.dark select option {
  background-color: #1e293b;
  color: #f1f5f9;
}

@media (prefers-color-scheme: dark) {
  select option {
    background-color: #1e293b;
    color: #f1f5f9;
  }
}
</style>
